import { useEffect, useRef } from 'react';

/**
 * Custom hook to manage map interaction locking
 * Disables specific map interactions while preserving DOM event handling
 * Only blocks map-specific interactions: dragging, zooming, keyboard shortcuts
 */
const useMapInteractionLock = (map, enabled) => {
  const previousStatesRef = useRef({});
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!map) return;

    // Store initial states on first run
    if (!isInitializedRef.current) {
      previousStatesRef.current = {
        dragging: map.dragging.enabled(),
        scrollWheelZoom: map.scrollWheelZoom.enabled(),
        doubleClickZoom: map.doubleClickZoom.enabled(),
        touchZoom: map.touchZoom.enabled(),
        boxZoom: map.boxZoom.enabled(),
        keyboard: map.keyboard.enabled(),
      };
      isInitializedRef.current = true;
    }

    if (enabled) {
      // Disable map-specific interactions only
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    } else {
      // Restore previous states
      const states = previousStatesRef.current;
      if (states.dragging) map.dragging.enable();
      if (states.scrollWheelZoom) map.scrollWheelZoom.enable();
      if (states.doubleClickZoom) map.doubleClickZoom.enable();
      if (states.touchZoom) map.touchZoom.enable();
      if (states.boxZoom) map.boxZoom.enable();
      if (states.keyboard) map.keyboard.enable();
    }

    // Cleanup: restore original states on unmount
    return () => {
      if (isInitializedRef.current && map) {
        const states = previousStatesRef.current;
        if (states.dragging) map.dragging.enable();
        if (states.scrollWheelZoom) map.scrollWheelZoom.enable();
        if (states.doubleClickZoom) map.doubleClickZoom.enable();
        if (states.touchZoom) map.touchZoom.enable();
        if (states.boxZoom) map.boxZoom.enable();
        if (states.keyboard) map.keyboard.enable();
      }
    };
  }, [map, enabled]);
};

export default useMapInteractionLock;